<script setup>
import { ref } from 'vue'
import { event } from 'vue-gtag'

//properties set by parent
const props = defineProps({
  fileName: {
    type: String,
    required: true,
  }
})

//reactive field proxies
const title = ref(null)
const imagePath = ref(null)
const link = ref(null)
const description = ref(null)
const indicator = ref(null)
const iconClass = ref(null)
const iconColor = ref(null)
const iconBackgroundColor = ref(null)


//Methods
async function getText(file) {
  let x = await fetch(file);
  let y = await x.text();
  let cardObj = JSON.parse(y);

  title.value = cardObj.title
  imagePath.value = import.meta.env.VITE_BASE_URL + cardObj.imagePath
  link.value = cardObj.link
  description.value = cardObj.description
  indicator.value = encodeURIComponent(cardObj.indicator)
  iconClass.value = cardObj.iconClass
  iconColor.value = cardObj.iconColor
  iconBackgroundColor.value = cardObj.iconBackgroundColor
}

const trackArticleClick = (title) => {
	event('article_link_click', {
		event_category: 'outbound',
		event_label: title
  })
}

//initial method run on page load
let url = import.meta.env.VITE_BASE_URL + "/json/" + props.fileName
getText(url);

</script>

<template>
	<div class="list-item-card">
			<div class="li-card-icon-section" :style="{ color: iconColor, backgroundColor: iconBackgroundColor }">
				<i :class="iconClass"></i>
			</div>
			<div class="li-card-content-section">
				<div class="li-card-title">{{ title }}</div>
				<div class="li-card-description">{{ description }}</div>
			</div>
			<div class="li-card-link-section">
				<a class="external-link no-underline" :href="link" target="_none" :title="title" @click="trackArticleClick(title)" >
					<i class="external-link-icon fa-solid fa-arrow-up-right-from-square"></i>
					<span class="external-link-text">Read this article</span>
				</a>
			</div>
	</div>
</template>

<style scoped>

.list-item-card {
	display: flex;
	flex-direction: row;
	width: 100%;	
	padding: 16px;
	justify-content: space-between;
	align-items: center;
	align-self: stretch;
	border-radius: var(--spacing-md, 8px);
	background: var(--surfaces-surface-default, #FFF);
	gap: 1rem;
	/* UI Card */
	box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.15);
}
.li-card-icon-section {
	display: flex;
	padding: 1.5rem;
	justify-content: center;
	align-items: center;
	border-radius: var(--spacing-md, 8px);
	font-size: 2rem;
}
.li-card-content-section {
	display: flex;
	flex-direction: column;
	align-items: start;
	/*gap: var(--spacing-lg, 16px);*/
	flex: 1 0 0;
}
.li-card-link-section {

}
.li-card-title {
	color: var(--text-Header, #171717);
	font-variant-numeric: lining-nums proportional-nums;
	font-feature-settings: 'liga' off, 'clig' off;

	/* Medium/H5 */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-H5, 25px);
	font-style: normal;
	font-weight: 500;
	line-height: var(--font-line-height-H5, 32px); /* 128% */

	margin-bottom: .5rem;
}
.li-card-description {
	color: var(--text-Primary, #2C2C2C);
	font-variant-numeric: lining-nums proportional-nums;
	font-feature-settings: 'liga' off, 'clig' off;

	/* Regular/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 16px);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 24px); /* 150% */
}
.external-link {
	font-variant-numeric: lining-nums proportional-nums;
	font-feature-settings: 'liga' off, 'clig' off;

	/* Medium/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 16px);    
    color: var(--text-Link);
    font-size: var(--font-size-Base);
    font-weight: var(--font-weight-Medium);
    cursor: pointer;
}
.external-link-icon {
	margin-right: .5rem
}
.no-underline {
	text-decoration: none;
}
/* 499px - Mobile */
@media (max-width: 31.1875rem) {
	.list-item-card {
		flex-direction: column;
		align-items: start;
		margin-bottom: 1rem;
	}
}

.card {
	/*display: flex;
	flex-direction: column;*/
	margin-bottom: 1.8rem;
	align-items: center;
	gap: 0.625rem;
	background: var(--surface-color-IndicatorBlue);
}
.image-container {
	position: relative; /* Establishes a positioning context for absolute children */
  	display: inline-block; /* Helps contain the image dimensions */
	width: 302px;
	height: 178px;
	background: var(--surface-color-IndicatorBlue);
	box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.10);
	border-radius: var(--radius-xl, 0.75rem);
}
.clickable {
	cursor:pointer;
}
.card-image {
	width: 100%;      /* Image takes the full width of its parent */
	height: 100%;     /* Image takes the full height of its parent */
	object-fit: cover; /* or contain, fill, etc. */
	border-radius: var(--radius-xl, 0.75rem);
	background: var(--surface-color-IndicatorBlue);
}
.card-content {
	/*display: flex;
	flex-direction: row;*/
	justify-content: left; /* Centers content horizontally (main axis) */
	align-items: left;   /* Centers content vertically (cross axis) */
	position: absolute; /* Positions the div relative to its parent container */
	top: 0; /* Aligns to the top edge of the container */
	left: 0; /* Aligns to the left edge of the container */
	width: 100%; /* Makes the overlay cover the full width */
	height: 100%; /* Makes the overlay cover the full height */
	background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent black background */
	color: var(--icons-icon-cta-reverse, #FFF);
	border-radius: var(--radius-xl, 0.75rem);
	padding: 1rem;
}
.card-title {
	padding-top: 75px;;
	color: var(--text-Inverse);
	font-family: var(--font-family-Title, Roboto);
	font-size: 20px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	text-transform: uppercase;
	overflow: hidden;
	color: var(--text-Inverse);
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 1; /* Number of lines to show */
	-webkit-box-orient: vertical;
	/*line-clamp: 1; /* Standard property for future support */
}
.card-description {
	/*display: flex;*/
	/*width: 278px;
	height: 41px;
	flex-direction: row;*/
	justify-content: left;

	overflow: hidden;
	color: var(--text-Inverse);
	text-overflow: ellipsis;
	/*white-space: nowrap;*/
	font-family: var(--font-family-Title, Roboto);
	font-size: 16px;
	font-style: normal;
	font-weight: 500;
	line-height: normal;
	margin-top: .5rem;
	display: -webkit-box;
	-webkit-line-clamp: 2; /* Number of lines to show */
	-webkit-box-orient: vertical;
	/*line-clamp: 2; /* Standard property for future support */

}
</style>